import React from "react";

import TimelineIcon from "./TimelineIcon";
import ZestComponent from "./ZestComponent";
import type { TimelineStatus } from "./types";

// React rebuild of the ZEST v2 Figma component set Zest::Timeline / Zest::TimelineIcon

export interface TimelineItem {
  // Stable identity for React keys — callers with meaningful, ordered steps
  // (e.g. AppointmentTimeline) supply one; blank spacer items may omit it.
  id?: string;
  status?: TimelineStatus;
  icon?: string | null;
  iconText?: string | null;
  primaryText?: string;
  secondaryText?: string;
}

export interface TimelineProps {
  // Blank entries (null, undefined, or an empty item) render as spacers.
  items: Array<TimelineItem | null | undefined>;
}

const isBlank = (item: TimelineItem | null | undefined): boolean =>
  !item || (!item.icon && !item.iconText && !item.primaryText && !item.secondaryText);

const keyFor = (item: TimelineItem | null | undefined, index: number): string =>
  item?.id ?? item?.primaryText ?? `item-${index}`;

const Timeline = ({ items }: TimelineProps): JSX.Element => (
  <ZestComponent name="timeline" className="w-full">
    <div className="relative h-px w-full bg-light top-3" />
    {/* An ordered list so the steps are conveyed as a numbered sequence, not a
        run of loose text. The connecting rule above stays a plain div — it's
        decorative and carries no semantics. `role="list"` is redundant on <ol>
        in principle, but Tailwind's `list-none` sets list-style:none, which
        makes Safari/VoiceOver drop the list semantics entirely. */}
    <ol role="list" className="relative flex justify-between gap-2 list-none pl-0 m-0">
      {items.map((item, index) => {
        if (isBlank(item)) return <li key={keyFor(item, index)} className="w-full flex-1" />;

        const step = item as TimelineItem;
        return (
          <li key={keyFor(step, index)} className="flex-1">
            <TimelineIcon
              status={step.status}
              icon={step.icon}
              iconText={step.iconText}
              primaryText={step.primaryText}
              secondaryText={step.secondaryText}
            />
          </li>
        );
      })}
    </ol>
  </ZestComponent>
);

export default Timeline;
